import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, BookMarked, Eye, EyeOff } from 'lucide-react';

const CATEGORIES = [
  { value: 'technique', label: 'Technique' },
  { value: 'materiaux', label: 'Matériaux' },
  { value: 'outils', label: 'Outils' },
  { value: 'entreprise', label: 'Entreprise' },
  { value: 'video', label: 'Vidéo' },
  { value: 'glossaire', label: 'Glossaire' },
];

const slugify = (str) =>
  str
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80);

export const KBAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', category: 'technique', summary: '', content: '',
    youtube_url: '', cover_url: '', is_published: true, order: 0,
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listKbArticles({ all_items: true });
      setArticles(data);
    } catch (e) {
      toast.error('Impossible de charger la base de connaissance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const reset = () => {
    setEditing(null);
    setForm({
      title: '', slug: '', category: 'technique', summary: '', content: '',
      youtube_url: '', cover_url: '', is_published: true, order: 0,
    });
  };

  const handleOpen = (article) => {
    if (article) {
      setEditing(article);
      setForm({
        title: article.title,
        slug: article.slug,
        category: article.category,
        summary: article.summary || '',
        content: article.content || '',
        youtube_url: article.youtube_url || '',
        cover_url: article.cover_url || '',
        is_published: article.is_published,
        order: article.order || 0,
      });
    } else {
      reset();
    }
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) {
      toast.error('Titre et slug sont obligatoires');
      return;
    }
    try {
      if (editing) {
        await api.updateKbArticle(editing.id, form);
        toast.success('Article mis à jour');
      } else {
        await api.createKbArticle(form);
        toast.success('Article créé');
      }
      setOpen(false);
      reset();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleTogglePublish = async (a) => {
    try {
      await api.updateKbArticle(a.id, { is_published: !a.is_published });
      toast.success(a.is_published ? 'Brouillon' : 'Publié');
      load();
    } catch {
      toast.error('Impossible de modifier');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Supprimer "${a.title}" ?`)) return;
    try {
      await api.deleteKbArticle(a.id);
      toast.success('Supprimé');
      load();
    } catch {
      toast.error('Impossible de supprimer');
    }
  };

  return (
    <div data-testid="kb-admin">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-light text-2xl text-[#1F2A4A]">Base de connaissance</h2>
          <p className="text-sm text-[#4A5568]">Articles, tutoriels et fiches techniques pour les élèves.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpen(null)} className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full" data-testid="kb-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouvel article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
              <DialogDescription>
                Cet article sera visible dans l'onglet "Base de connaissance" des élèves.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={form.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setForm((f) => ({
                      ...f,
                      title,
                      slug: editing ? f.slug : slugify(title),
                    }));
                  }}
                  required
                  data-testid="kb-form-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Slug (URL)</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    required
                    data-testid="kb-form-slug"
                  />
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="kb-form-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Résumé (1–2 phrases)</Label>
                <Textarea
                  value={form.summary}
                  onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  rows={2}
                  data-testid="kb-form-summary"
                />
              </div>
              <div>
                <Label>Contenu (markdown)</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  rows={5}
                  className="font-mono text-sm"
                  data-testid="kb-form-content"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>URL vidéo YouTube (optionnel)</Label>
                  <Input
                    value={form.youtube_url}
                    onChange={(e) => setForm((f) => ({ ...f, youtube_url: e.target.value }))}
                    placeholder="https://youtube.com/..."
                    data-testid="kb-form-youtube"
                  />
                </div>
                <div>
                  <Label>URL image de couverture</Label>
                  <Input
                    value={form.cover_url}
                    onChange={(e) => setForm((f) => ({ ...f, cover_url: e.target.value }))}
                    placeholder="https://..."
                    data-testid="kb-form-cover"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 items-center">
                <div>
                  <Label>Ordre</Label>
                  <Input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))}
                    data-testid="kb-form-order"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))}
                    data-testid="kb-form-published"
                  />
                  <Label className="cursor-pointer">{form.is_published ? 'Publié' : 'Brouillon'}</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" className="bg-[#1F2A4A] text-white hover:bg-[#324166]" data-testid="kb-form-submit">
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-[#4A5568]">Chargement…</p>
      ) : articles.length === 0 ? (
        <div className="text-center py-16 bg-[#FAF7F0] border border-dashed border-[#1F2A4A]/15 rounded-xl">
          <BookMarked className="w-12 h-12 text-[#C9B896] mx-auto mb-4" />
          <p className="text-[#4A5568]">Aucun article. Crée le premier !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.id} className="bg-white border border-[#1F2A4A]/10 rounded-xl p-4 flex items-center gap-4" data-testid={`kb-item-${a.slug}`}>
              <div className="w-9 h-9 rounded-full bg-[#C9B896]/15 flex items-center justify-center flex-shrink-0">
                <BookMarked className="w-4 h-4 text-[#1F2A4A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1F2A4A] truncate">{a.title}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-[#4A5568]">
                  <Badge variant="outline" className="text-[10px]">{CATEGORIES.find((c) => c.value === a.category)?.label}</Badge>
                  <span>/{a.slug}</span>
                  {!a.is_published && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Brouillon</Badge>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleTogglePublish(a)} title={a.is_published ? 'Dépublier' : 'Publier'}>
                {a.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-amber-700" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleOpen(a)} data-testid={`kb-edit-${a.slug}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(a)} className="text-red-600 hover:bg-red-50" data-testid={`kb-delete-${a.slug}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
